import { useEffect, useRef, useState } from 'react';
import { CountUp } from '../../components/CountUp';
import { formatPercent } from '../../design/format';

/**
 * The SUCCESS-PROBABILITY HEADLINE (PLAN.md M7 screen 2) — the dashboard's hero number, with a
 * count-up on load (the one restrained "spectacle") AND a confidence RING that sweeps to the
 * probability. Big display-serif percentage, a tone derived from the probability, and a one-line
 * gloss ("of simulated retirements survived the full horizon").
 *
 * The ring is a lightweight inline SVG (no ECharts instance) so the hero stays cheap and crisp; its
 * arc draws in via `stroke-dashoffset` and honours reduced motion. Colour comes from the same tone
 * thresholds as the number, so the ring and the figure read as one instrument.
 */
export interface SuccessHeadlineProps {
  /** `ResultSet.successProbability` in [0,1]. */
  probability: number;
  /** Number of paths the probability was computed over (for the caption). */
  paths: number;
  /** Whether the run used illustrative synthetic data (label it honestly). */
  illustrative?: boolean;
}

/** Tone (a design token var) for a probability — the shared success thresholds. */
function toneFor(probability: number): string {
  return probability >= 0.85 ? 'var(--pos)' : probability >= 0.6 ? 'var(--warn)' : 'var(--neg)';
}

export function SuccessHeadline({
  probability,
  paths,
  illustrative,
}: SuccessHeadlineProps): React.ReactElement {
  const tone = toneFor(probability);
  return (
    <div className="card reveal success-headline" style={{ gridColumn: '1 / -1' }}>
      <div className="success-headline__text">
        <div className="card__title">Probability of success</div>
        <div
          className="num num--lit success-headline__figure"
          style={{ fontFamily: 'var(--font-display)', color: tone }}
        >
          <CountUp value={probability} duration={1000} format={(n) => formatPercent(n)} />
        </div>
        <p className="success-headline__gloss">
          of {paths.toLocaleString('en-US')} simulated paths kept the plan funded for its full
          horizon.
          {illustrative ? ' Illustrative synthetic returns — not validated market history.' : ''}
        </p>
      </div>
      <ConfidenceRing probability={probability} tone={tone} />
    </div>
  );
}

interface ConfidenceRingProps {
  probability: number;
  /** A CSS colour (design token var) matching the headline figure. */
  tone: string;
}

/**
 * A confidence ring — an SVG arc that sweeps clockwise from 12 o'clock to `probability` of a full
 * turn. Draws in on mount (reduced-motion snaps). The track is the hairline; the arc is `tone`.
 */
function ConfidenceRing({ probability, tone }: ConfidenceRingProps): React.ReactElement {
  const size = 132;
  const stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(1, probability));

  const [drawn, setDrawn] = useState(false);
  const rafRef = useRef(0);
  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setDrawn(true);
      return;
    }
    // Next frame so the transition fires from the 0 state.
    rafRef.current = requestAnimationFrame(() => setDrawn(true));
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const offset = drawn ? c * (1 - clamped) : c;

  return (
    <svg
      className="success-headline__ring"
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={`Success probability ${formatPercent(probability)}`}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="var(--line)"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={tone}
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={offset}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
        style={{ transition: 'stroke-dashoffset var(--dur-slow) var(--ease-out)' }}
      />
    </svg>
  );
}
